/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from './constants';
import { authOnlyHeaders } from './use-publish-dialog';

/**
 * Required OLX fields the property still lacks (``category``, ``price``,
 * ``zipcode``, ``bedrooms``), from ``GET .../olx/readiness``. Empty while
 * loading or when the check fails: the worker still refuses an incomplete ad.
 *
 * @param agencyId - Agency id.
 * @param propertyId - Property id.
 * @param active - Whether OLX is offered in the dialog at all.
 * @returns The missing field keys.
 */
export function useOlxReadiness(agencyId: string, propertyId: string, active: boolean): string[] {
  const [missing, setMissing] = useState<string[]>([]);
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    (async () => {
      const headers = await authOnlyHeaders();
      const url = `${API_BASE}/portal-integration/agencies/${agencyId}/properties/${propertyId}/olx/readiness`;
      const res = await axios.get(url, { headers }).catch(() => null);
      const list = res?.data?.missing;
      if (!cancelled) setMissing(Array.isArray(list) ? list.filter((f: unknown) => typeof f === 'string') : []);
    })();
    return () => { cancelled = true; };
  }, [agencyId, propertyId, active]);
  return missing;
}

/**
 * Everything the property must get before OLX can be chosen: the fields the
 * backend reported plus photos (checked here, like Instagram).
 */
export function olxMissingFields(readinessMissing: string[], hasImages: boolean): string[] {
  return hasImages ? readinessMissing : [...readinessMissing, 'images'];
}
